import type { Href } from 'expo-router';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { trackEvent } from '@/modules/analytics';
import {
  checkEligibility,
  validateAndUseInviteCode as apiValidateAndUseInviteCode,
} from '@/modules/api';
import type { PartnerOrg } from '@/modules/api';
import type { User } from '@/types/user';

import type { EligibilityCheckResult } from '../types/eligibilityCheckResult';
import type { OnboardingDataDraft } from '../types/onboardingDataDraft';
import type { PartnersState } from '../types/partnersState';
import { buildCheckEligibilityPayload } from '../utils/buildCheckEligibilityPayload';
import { resolveSelectedOrg } from '../utils/resolveSelectedOrg';

type UseEligibilityCheckArgs = {
  draft: OnboardingDataDraft;
  setDraft: Dispatch<SetStateAction<OnboardingDataDraft>>;
  partners: PartnersState;
  decodedOrgParam: string | null;
  /** Raw `?org=` param — the isRestarting-release effect watches it. */
  orgParam: string | undefined;
  /**
   * For SSO users, the bare uuid of `customerProfile.ssoPayingOrganization`
   * (derived in the provider). Matched against the catalog's `webserverOrgUuid`
   * to hydrate `selectedOrg` + the draft org, skipping company-search.
   */
  ssoWebserverOrgUuid?: string;
  /** Core's user refresh — called on a passing eligibility/invite check. */
  refreshUser: () => Promise<User>;
  /** Core's route setter — restartEligibility routes to a bare company-search. */
  setCurrentRoute: Dispatch<SetStateAction<Href | null>>;
};

export type UseEligibilityCheck = {
  selectedOrg: PartnerOrg | undefined;
  /**
   * Mirror of `selectedOrg`, assigned DURING RENDER (not in an effect) — read by
   * the core's goToNextStep. Returned so core lists it in goToNextStep's deps.
   */
  selectedOrgRef: RefObject<PartnerOrg | undefined>;
  setSelectedOrg: (selection: PartnerOrg | undefined) => void;
  restartEligibility: () => void;
  runEligibilityCheck: (options?: { isRetry?: boolean }) => Promise<void>;
  validateAndUseInviteCode: () => Promise<void>;
  eligibilityCheckResult: EligibilityCheckResult | undefined;
  setEligibilityCheckResult: Dispatch<SetStateAction<EligibilityCheckResult | undefined>>;
};

/**
 * Eligibility-check sub-flow: org selection (derived `selectedOrg` + the entry
 * `?org=` hint consumption), the eligibility-result state, and the
 * check/invite-code/restart actions. Reads `draft`/`partners` from the core
 * provider; writes outcomes into `eligibilityCheckResult` (the driver routes from
 * it). Extracted verbatim from RulesEngineProvider.
 */
export function useEligibilityCheck({
  draft,
  setDraft,
  partners,
  decodedOrgParam,
  orgParam,
  ssoWebserverOrgUuid,
  refreshUser,
  setCurrentRoute,
}: UseEligibilityCheckArgs): UseEligibilityCheck {
  // "Start over" latch. While true, `selectedOrg` ignores the `?org=` URL hint so
  // the resolver routes to /step/company-search (and the layout navigates to the
  // bare route, dropping the param) instead of the driver bouncing back to the
  // just-cleared org's entry screen. Cleared once the param is gone.
  const [isRestarting, setIsRestarting] = useState(false);
  // Latch: once the org is explicitly de-selected (Start Over / back / clear),
  // stop auto-applying the SSO org for the rest of the session. Unlike the
  // `?org=` hint (physically dropped from the URL on restart), the SSO org is
  // derived from the persistent user object, so it can't be "dropped" — without
  // this latch the consume effect + resolver SSO branch would immediately
  // re-select it and trap the user on the org's entry screen. The SSO org is
  // authoritative and can't change mid-session, so the latch is sticky: after an
  // opt-out the user drives company-search manually (a fresh session re-applies).
  const [ssoAutoSelectSuppressed, setSsoAutoSelectSuppressed] = useState(false);
  const [eligibilityCheckResult, setEligibilityCheckResult] = useState<
    EligibilityCheckResult | undefined
  >();

  // Reset the opt-out latch when the user identity changes (the SSO org's uuid
  // changes value or clears). RulesEngineProvider stays mounted across
  // resetUserData() / sign-out → sign-in, so without this a prior user's
  // de-select — even a non-SSO user clearing company-search — would leave the
  // latch set and block the NEXT SSO user's auto-skip. Done DURING RENDER (React's
  // "adjust state when a prop changes" pattern) rather than an effect, so the
  // reset is synchronous: no extra render where `ssoSelectUuid` is briefly stale.
  // A same-user refreshUser re-fetch yields the same urn, so this never fires for
  // the same user — only on a genuine identity change.
  const prevSsoWebserverOrgUuidRef = useRef(ssoWebserverOrgUuid);
  if (prevSsoWebserverOrgUuidRef.current !== ssoWebserverOrgUuid) {
    prevSsoWebserverOrgUuidRef.current = ssoWebserverOrgUuid;
    if (ssoAutoSelectSuppressed) setSsoAutoSelectSuppressed(false);
  }

  // The SSO uuid actually used for auto-selection — gated by the suppress latch.
  // Feeds BOTH the selectedOrg derivation and the consume effect so a de-selected
  // SSO org stays de-selected.
  const ssoSelectUuid = ssoAutoSelectSuppressed ? undefined : ssoWebserverOrgUuid;

  // `selectedOrg` is driven by rules-engine state first: when the user picks an
  // org (or a valid entry `?org=` hint is consumed), the draft carries the org
  // forward. The URL hint is only read on entry routes so stale params on later
  // screens don't bounce the user back into that org's eligibility branch.
  const selectedOrg = useMemo(
    () =>
      resolveSelectedOrg({
        isRestarting,
        draft,
        partners,
        decodedOrgParam,
        ssoWebserverOrgUuid: ssoSelectUuid,
      }),
    // resolveSelectedOrg reads only the org-* draft fields; depending on the
    // whole `draft` would recompute (and churn selectedOrg's identity) on every
    // unrelated keystroke, so we list the org fields explicitly as before.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      partners,
      draft.orgKey,
      draft.orgUuid,
      draft.orgName,
      draft.orgEligibilityCheckFields,
      decodedOrgParam,
      ssoSelectUuid,
      isRestarting,
    ]
  );

  // Mirror state into refs so the stable async actions read the latest values
  // without listing them as deps — an unstable identity used to cascade into
  // eligibility-check loops in CompanySearchScreen.
  const draftRef = useRef(draft);
  const selectedOrgRef = useRef(selectedOrg);
  // Mirrored DURING RENDER, not in the sync effect: runEligibilityCheck fires
  // from CompanySearchScreen's auto-advance effect, which runs before this
  // provider's sync effect on the same commit. An effect-synced ref would still
  // be stale there — capturing supportsEligibilityCheck: undefined.
  selectedOrgRef.current = selectedOrg;
  // De-dupes the `sso-org-not-found` event: the consume effect below depends on
  // `partners`, so any re-emission of a `ready` state would re-enter the
  // no-match branch and re-track. Fire once per distinct SSO uuid.
  const notFoundTrackedForUuidRef = useRef<string | undefined>(undefined);

  const setSelectedOrg = useCallback(
    (selection: PartnerOrg | undefined) => {
      // Changing the org invalidates any prior org's eligibility result, so a
      // stale result can't short-circuit routing by the OLD org's fields.
      setEligibilityCheckResult(undefined);
      // An explicit de-select (Start Over, back-to-company-search, clear) means
      // the user is opting out of the SSO auto-skip — latch it so the SSO org
      // isn't immediately re-applied. No-op for non-SSO users.
      if (!selection) setSsoAutoSelectSuppressed(true);
      setDraft((prev) => {
        if (!selection) {
          return { ...prev, orgKey: '', orgUuid: '', orgName: '', orgEligibilityCheckFields: '' };
        }
        const org =
          partners.status === 'ready' && Array.isArray(partners.data)
            ? partners.data.find(({ key }) => key === selection.key)
            : undefined;
        const selected = org ?? selection;
        return {
          ...prev,
          orgKey: selected.key,
          orgUuid: selected.uuid,
          orgName: selected.name,
          orgEligibilityCheckFields: selected.eligibilityCheckFields,
        };
      });
    },
    [partners, setDraft]
  );

  // "Start over" reset: clear the selected org (which also clears the eligibility
  // result via setSelectedOrg) and route to a bare /step/company-search. The
  // `isRestarting` latch makes `selectedOrg` ignore the lingering `?org=` URL hint
  // until the layout's redirect drops it, so the driver resolves straight to
  // company-search instead of bouncing back to the just-cleared org's entry screen
  // (which would oscillate into an update loop).
  const restartEligibility = useCallback(() => {
    setSelectedOrg(undefined);
    setIsRestarting(true);
    setCurrentRoute('/step/company-search');
  }, [setSelectedOrg, setCurrentRoute]);

  // Validates + consumes the invite code in the draft. Writes the outcome into
  // `eligibilityCheckResult`; the driver routes from it (pass → /eligible, fail
  // → retry screen). On a pass we refreshUser first so the backend-assigned
  // payingOrganization (and its image) is in state before /eligible renders —
  // the driver still shows /eligible because the resolver keys that on
  // `passed === true`, above the payingOrganization gate.
  const validateAndUseInviteCode = useCallback(async () => {
    const selectedOrg = selectedOrgRef.current;
    const inviteCode = draftRef.current.inviteCode?.trim() ?? '';
    const result: EligibilityCheckResult = {
      isChecking: false,
      supportsEligibilityCheck: selectedOrg?.supportsEligibilityCheck,
      eligibilityCheckFields: selectedOrg?.eligibilityCheckFields,
      org: selectedOrg?.key,
    };
    setEligibilityCheckResult({ isChecking: true });
    try {
      await apiValidateAndUseInviteCode(inviteCode);
      result.passed = true;
    } catch {
      result.passed = false;
    } finally {
      // Swallow refresh failures — eligibility succeeded, so a transient refresh
      // error shouldn't block the flow (the image just won't be present yet).
      if (result.passed) {
        try {
          await refreshUser();
        } catch {
          /* keep advancing */
        }
      }
      setEligibilityCheckResult(result);
    }
  }, [refreshUser]);

  // Runs the eligibility check for the selected org + draft. Like invite-code
  // validation, it writes `eligibilityCheckResult` (the driver routes from it)
  // and refreshes the user on a pass for the paying-org image. Reads draft +
  // selectedOrg via refs to keep the callback identity stable (CompanySearchScreen
  // includes it in its auto-advance deps — an unstable identity used to loop).
  const runEligibilityCheck = useCallback(
    async (options?: { isRetry?: boolean }) => {
      const selectedOrg = selectedOrgRef.current;
      const result: EligibilityCheckResult = {
        isChecking: false,
        isRetry: options?.isRetry === true,
        supportsEligibilityCheck: selectedOrg?.supportsEligibilityCheck,
        eligibilityCheckFields: selectedOrg?.eligibilityCheckFields,
        org: selectedOrg?.key,
      };
      try {
        setEligibilityCheckResult({ isChecking: true });
        // Sparta's success signal is `successfulMatchMethod` on the response; an
        // HTTP 200 with `{}` (or only code/message) is a soft no-match, not a pass.
        const response = await checkEligibility(buildCheckEligibilityPayload(draftRef.current));
        result.passed = Boolean(response.successfulMatchMethod);
        // On a soft no-match the backend returns a `code` (200 with { code,
        // message }). Carry it so the driver can route terminal/not-qualified
        // failures to /step/not-eligible instead of the retry screen.
        if (!result.passed) {
          result.code = response.code;
        }
      } catch {
        result.passed = false;
      } finally {
        if (result.passed) {
          try {
            await refreshUser();
          } catch {
            /* keep advancing — eligibility succeeded */
          }
        }
        setEligibilityCheckResult(result);
      }
    },
    [refreshUser]
  );

  // Keep draftRef fresh for the stable async actions (selectedOrgRef is mirrored
  // during render above).
  useEffect(() => {
    draftRef.current = draft;
  });

  // Consume the SSO paying org into the draft. SSO users are provisioned with
  // `customerProfile.ssoPayingOrganization` but no top-level payingOrganization;
  // matching it against the catalog (by `webserverOrgUuid`) lets the resolver
  // skip company-search. Writing it into the draft also makes CompanySearchScreen
  // treat it as a confirmed selection — so FN_LN_DOB orgs auto-fire the check
  // (LoadingScreen) instead of rendering the search form.
  //
  // MUST precede the `?org=` consume effect below: both bail on an existing
  // `prev.orgKey`, so whichever enqueues its setDraft first wins. Running SSO
  // first enforces resolveSelectedOrg's "SSO outranks `?org=`" precedence — a
  // provisioned SSO user with a stray deep link still lands on their SSO org.
  // If the catalog has no matching partner the skip can't proceed: fire a
  // tracked event (the data issue stays observable) and bail WITHOUT writing the
  // draft, letting the `?org=` effect below act as a fallback (and otherwise the
  // resolver lands the user on the normal company-search form).
  useEffect(() => {
    if (!ssoSelectUuid || draft.orgKey || partners.status !== 'ready' || !partners.data) {
      return;
    }
    const org = partners.data.find(
      (partner) => partner.webserverOrgUuid && partner.webserverOrgUuid === ssoSelectUuid
    );
    if (!org) {
      // Track once per uuid (see notFoundTrackedForUuidRef). Org identifier
      // only — no PII.
      if (notFoundTrackedForUuidRef.current !== ssoSelectUuid) {
        notFoundTrackedForUuidRef.current = ssoSelectUuid;
        trackEvent('sso-org-not-found', { webserverOrgUuid: ssoSelectUuid });
      }
      return;
    }
    setDraft((prev) => {
      if (prev.orgKey) return prev;
      return {
        ...prev,
        orgKey: org.key,
        orgUuid: org.uuid,
        orgName: org.name,
        orgEligibilityCheckFields: org.eligibilityCheckFields,
      };
    });
  }, [ssoSelectUuid, draft.orgKey, partners, setDraft]);

  // Consume a valid entry `?org=` hint into the draft. The URL param is only an
  // email/deep-link hint; once the partner catalog has resolved it, downstream
  // routing should use rules-engine state instead of carrying `?org=` from
  // screen to screen. Runs after the SSO consume above, so it only claims the
  // draft when no SSO org has (see the precedence note there).
  useEffect(() => {
    if (!decodedOrgParam || draft.orgKey || partners.status !== 'ready' || !partners.data) {
      return;
    }
    const org = partners.data.find(
      (partner) => partner.key === decodedOrgParam || partner.name.toLowerCase() === decodedOrgParam
    );
    if (!org) return;
    setDraft((prev) => {
      if (prev.orgKey) return prev;
      return {
        ...prev,
        orgKey: org.key,
        orgUuid: org.uuid,
        orgName: org.name,
        orgEligibilityCheckFields: org.eligibilityCheckFields,
      };
    });
  }, [decodedOrgParam, draft.orgKey, partners, setDraft]);

  // Release the "start over" latch once the `?org=` param is gone (the layout has
  // navigated to the bare /step/company-search) — from then on `selectedOrg`
  // derives normally again.
  useEffect(() => {
    if (isRestarting && !orgParam) {
      setIsRestarting(false);
    }
  }, [isRestarting, orgParam]);

  return {
    selectedOrg,
    selectedOrgRef,
    setSelectedOrg,
    restartEligibility,
    runEligibilityCheck,
    validateAndUseInviteCode,
    eligibilityCheckResult,
    setEligibilityCheckResult,
  };
}
